import { Box, Card, Grid, List, ListItem, Switch, Tab, Tabs, Divider } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';
import PropTypes from "prop-types";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import { Link, useNavigate } from "react-router-dom";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { wrapper, deviceViewWrapper, loadingScreen } from "utils/wrapper";
import { useEffect, useState } from "react";

import userAPI from "services/api/user";
import translationsAPI from "services/api/translations";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import moment from "moment";
import LogoutButton from "components/Buttons/LogoutButton";
import EmailSettings from "pages/components/Settings/EmailSettings";
import SystemAppearance from "pages/components/Settings/SystemAppearance";
import Blacklist from "pages/components/Settings/Blacklist";
import ChangePassword from "pages/components/Settings/ChangePassword";
import DeploymentSwitch from "pages/components/Settings/DeploymentSwitch";
import { getUserSettingsItems } from "utils/setup";
import TranslationsView from "pages/components/Settings/Translations";

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ px: 1, py: 0, height: '100%' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function SettingsView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, hasPassword, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    userAPI.loadToken(useAuth0());
    translationsAPI.loadToken(useAuth0());

    const [selectedType, setSelectedType] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [cautionView, setCautionView] = useState(true);

    const handleTabClick = (event, newValue) => {
        setActiveTab(newValue);
    };

    const queryClient = useQueryClient();

    const { mutateAsync } = useMutation({
        mutationFn: (values) => {
            return userAPI.processSubscription(values)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getSubscriptionsForUser', user.user.email] });
        }
    })

    const { mutate: toggleActive } = useMutation({
        mutationFn: (body) => {
            return userAPI.updateSubscriptionStatus(body);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getSubscriptionsForUser', user.user.email] });
        },
    });

    const { mutate: updateMode } = useMutation({
        mutationFn: (body) => {
            return userAPI.updateSystemMode({ appearance_type: body });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getUser', user.user.email] });
        },
    });

    const translationTypes = useQuery({
        queryKey: ['translationTypes', selectedOrg],
        queryFn: async () => {
            return await translationsAPI.getTranslationTypes();
        }
    });

    useEffect(() => {
        if (!translationTypes.isLoading && translationTypes.data?.length > 0) {
            if (activeTab == 5) {
                setSelectedType('blacklist');
            }
            else {
                setSelectedType(translationTypes.data.filter(row => row.type != 'blacklist')[0].type);
            }
        }

    }, [translationTypes.isLoading, translationTypes.data, selectedOrg, activeTab]);

    useEffect(() => {
        setActiveTab(0);
    }, [selectedOrg]);

    let tabs = [];

    tabs.push({
        id: "set_tab:appearance",
        value: 0,
        label: 'System Visuals',
        component: <SystemAppearance {...{ user, updateMode }} />
    });

    tabs.push({
        id: "set_tab:deployment",
        value: 1,
        label: 'Switch Deployments',
        component: <DeploymentSwitch {...{ user, selectedOrg, deviceLayout }} />
    });

    if (hasPassword) {
        tabs.push({
            id: "set_tab:password",
            value: 2,
            label: 'Password Manager',
            component: <ChangePassword {...{ user }} />
        });
    }

    tabs.push({
        id: "set_tab:email",
        value: 3,
        label: 'Email Configuration',
        component: <EmailSettings {...{ user, toggleActive, onSubmit: mutateAsync }} />
    });

    tabs.push({
        id: "set_tab:translations",
        value: 4,
        label: 'Nicknames & Phonetics',
        component: <TranslationsView {...{ translationTypes, selectedType, setSelectedType }} />
    });

    tabs.push({
        id: "set_tab:blacklist",
        value: 5,
        label: 'Translations Blacklist',
        component: <Blacklist {...{ translationTypes, selectedType, cautionView, setCautionView }} />
    });

    const filtered_tabs = tabs.filter((tab) => getUserSettingsItems(userPermissions, tab)).filter((tab) => !(user.user.organisations.length < 2 && tab.value == 1)).filter((tab) => !(selectedType == null && tab.value == 5));

    const tabGeneration = () => {
        return filtered_tabs.map(function (tab, index) {
            return <Tab key={tab.value} sx={{ minWidth: "fit-content !important", padding: '12px !important' }} value={index} label={tab.label} {...a11yProps(tab.value)} />
        });
    }

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 3.75rem - 16px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 3.75rem - 16px)"}
            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].base, 0.7), overflowY: 'hidden' })}
        >
            <SoftBox mt={1} px={1} display="flex" justifyContent="center" sx={{ height: `calc(100% - 16px)` }}>

                {deviceLayout != 'mobile' ? (
                    <Card sx={({ palette, functions: { rgba } }) => ({ height: '100%', width: '100%', background: rgba(palette[appearance].card, 1) })}>
                        <SoftBox display="flex" flexDirection="column" height="100%">
                            <SoftBox sx={({ palette, functions: { rgba } }) => ({ padding: '16px 8px 16px 16px', borderRadius: '16px 0 0 0', backgroundColor: rgba(palette[appearance].header, 1), borderBottom: `1px solid ${rgba(palette[appearance].border, 1)}` })}>
                                <SoftTypography variant="h5" fontWeight="bold" color={appearance}>Account Settings</SoftTypography>
                            </SoftBox>
                            <SoftBox height="100%" display="flex" flexDirection="row" sx={{maxHeight: 'calc(100svh - 10.25rem - 16px)'}}>
                                <SoftBox display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].content, 1), borderRight: `1px solid ${rgba(palette[appearance].border, 1)}` })}>
                                    <SoftBox width={'260px'}>
                                        <SoftBox p={1}>
                                            <List>
                                                {filtered_tabs.map((tab, index) => (
                                                    <ListItem key={tab.value}>
                                                        <SoftBox width='100%' display='flex' flexDirection="column">
                                                            <SoftBox my={0.5} px={1} onClick={() => { setActiveTab(index) }} sx={({ palette, functions: { rgba } }) => ({ borderRadius: '5px', cursor: 'pointer', backgroundColor: index == activeTab && rgba(palette[appearance].border, 1), transition: 'background-color 150ms ease-in-out' })}>
                                                                <SoftTypography py={2} variant="subtitle2" color={appearance}>{tab.label}</SoftTypography>
                                                            </SoftBox>
                                                            {(index + 1 < filtered_tabs.length) && (
                                                                <Divider />
                                                            )}
                                                        </SoftBox>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </SoftBox>
                                    </SoftBox>
                                    <SoftBox sx={{ width: 'calc(100% - 16px)', p: 1 }}>
                                        <LogoutButton>
                                            <SoftButton variant='gradient' fullWidth color={appearance}>
                                                Log out
                                            </SoftButton>
                                        </LogoutButton>
                                    </SoftBox>
                                </SoftBox>
                                {filtered_tabs[activeTab]?.component}
                            </SoftBox>
                        </SoftBox>
                    </Card>
                ) : (
                    <SoftBox height="100%" width="100%" display="flex" flexDirection="column">
                        <Grid container>
                            <Grid item xxs={12}>
                                <SoftBox>
                                    <Tabs value={activeTab} onChange={handleTabClick} aria-label="SettingsView Tabs"
                                        allowScrollButtonsMobile
                                        variant="scrollable"
                                        scrollButtons
                                        sx={{
                                            [`& .${tabsClasses.scrollButtons}`]: {
                                                '&.Mui-disabled': { opacity: 0 },
                                            },
                                            '& .MuiTabs-indicator': {
                                                top: '3px',
                                                left: activeTab == 0 ? '1px !important' : '0px',
                                                height: 'calc(100% - 6px)'
                                            }
                                        }}
                                    >
                                        {tabGeneration()}
                                    </Tabs>
                                </SoftBox>
                            </Grid>
                        </Grid>
                        <SoftBox height="100%" mt={1}>
                            <Card sx={({ palette, functions: { rgba } }) => ({ height: '100%', width: '100%', background: rgba(palette[appearance].card, 1) })}>
                                <SoftBox sx={{ overflowY: 'auto' }}>
                                    {filtered_tabs.map((tab, index) => (
                                        <CustomTabPanel value={activeTab} key={tab.value} index={index}>
                                            {filtered_tabs[activeTab]?.component}
                                        </CustomTabPanel>
                                    ))}
                                </SoftBox>
                            </Card>
                        </SoftBox>
                    </SoftBox>
                )}
            </SoftBox>
        </SoftBox>
    ), controller);
}

SettingsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default SettingsView;